import { useState, useCallback, useEffect, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate, useLocation } from "react-router-dom";
import api from "./utils/api";

import Home from "./pages/Home";
import Navbar from "./components/Navbar";
import AuthModal from "./components/AuthModal";
import PrivacyBanner from "./components/PrivacyBanner";
import { ToastProvider } from "./context/ToastContext";

const EditorPage        = lazy(() => import('./pages/EditorPage'));
const PostPage          = lazy(() => import('./pages/PostPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const DraftsPage        = lazy(() => import('./pages/DraftsPage'));
const DiscoverPage      = lazy(() => import('./pages/DiscoverPage'));
const ProfilePage       = lazy(() => import('./pages/ProfilePage'));
const AboutPage         = lazy(() => import('./pages/AboutPage'));
const ContactPage       = lazy(() => import('./pages/ContactPage'));
const AuthorPage        = lazy(() => import('./pages/AuthorPage'));
const PrivacyPage       = lazy(() => import('./pages/PrivacyPage'));
const InterviewsPage    = lazy(() => import('./pages/InterviewsPage'));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}
function PostPageWrapper({ user }) {
  const { slug } = useParams();
  return <PostPage slug={slug} user={user} />;
}

function ProtectedRoute({ user, children }) {
  if (!user?.is_admin) return <p style={{ textAlign: "center", marginTop: 60 }}>Access denied — admins only.</p>;
  return children;
}

function AuthRedirect({ openAuth, mode }) {
  const navigate = useNavigate();
  useEffect(() => {
    openAuth(mode);
    navigate("/", { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function App() {
  const [user, _setUser] = useState(() => {
    try {
      const stored = localStorage.getItem("user");
      return stored ? JSON.parse(stored) : { is_authenticated: false };
    } catch {
      return { is_authenticated: false };
    }
  });

  const [authModal, setAuthModal] = useState(null); // null | 'signin' | 'signup' | 'forgot'
  const openAuth  = useCallback((mode = "signin") => setAuthModal(mode), []);
  const closeAuth = useCallback(() => setAuthModal(null), []);

  const setUser = useCallback((userData) => {
    if (userData?.is_authenticated) {
      localStorage.setItem("user", JSON.stringify(userData));
    } else {
      localStorage.removeItem("user");
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
    }
    _setUser(userData);
  }, []);

  // Keep avatar and is_super_editor in user context fresh after login
  useEffect(() => {
    if (!user?.is_authenticated) return;
    api.get("/api/auth/profile/")
      .then(res => {
        _setUser(prev => ({
          ...prev,
          avatar: res.data.avatar || "",
          is_super_editor: res.data.is_super_editor || false,
        }));
      })
      .catch(() => {});
  }, [user?.is_authenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ToastProvider>
      <Router>
        <ScrollToTop />
        <Navbar user={user} setUser={setUser} onOpenAuth={openAuth} />

        {authModal && (
          <AuthModal mode={authModal} onClose={closeAuth} setUser={setUser} />
        )}

        <PrivacyBanner />
        <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Home user={user} />} />

          <Route
            path="/editor"
            element={
              <ProtectedRoute user={user}>
                <EditorPage user={user} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/editor/:slug"
            element={
              <ProtectedRoute user={user}>
                <EditorPage user={user} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/drafts"
            element={
              <ProtectedRoute user={user}>
                <DraftsPage user={user} />
              </ProtectedRoute>
            }
          />

          <Route path="/discover" element={<DiscoverPage user={user} />} />
          <Route path="/discover/category/:categorySlug" element={<DiscoverPage user={user} />} />
          <Route path="/discover/tag/:tagSlug" element={<DiscoverPage user={user} />} />
          <Route path="/interviews" element={<InterviewsPage user={user} />} />

          <Route path="/login"          element={<AuthRedirect openAuth={openAuth} mode="signin" />} />
          <Route path="/register"       element={<AuthRedirect openAuth={openAuth} mode="signup" />} />
          <Route path="/forgot-password" element={<AuthRedirect openAuth={openAuth} mode="forgot" />} />
          <Route path="/reset-password/:uid/:token" element={<ResetPasswordPage />} />

          <Route path="/posts/:slug" element={<PostPageWrapper user={user} />} />
          <Route path="/profile" element={<ProfilePage user={user} setUser={setUser} />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/authors/:username" element={<AuthorPage user={user} />} />
        </Routes>
        </Suspense>
      </Router>
    </ToastProvider>
  );
}

export default App;
